import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Link } from "react-router-dom";
import CommunityEventCard from "./CommunityEventCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/lib/price-utils";

const CommunityRunsSection = () => {
  const { data: communityEvents, isLoading } = useQuery({
    queryKey: ['community-events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('id, slug, title, event_date, city, country, distance_km, current_participants, max_participants, price_cents, currency, image_url, community_vibe, after_activity, participation_type, meeting_point')
        .eq('status', 'published')
        .eq('event_category', 'community')
        .order('event_date', { ascending: true })
        .limit(4);

      if (error) throw error;
      return data;
    },
  });

  // Don't render section if no community events
  if (!isLoading && (!communityEvents || communityEvents.length === 0)) {
    return null;
  }

  const events = communityEvents?.map(event => ({
    id: event.id,
    slug: event.slug,
    title: event.title,
    date: format(new Date(event.event_date), "EEEE d MMMM yyyy", { locale: fr }),
    location: event.meeting_point || `${event.city}, ${event.country}`,
    distance: event.distance_km ? `${event.distance_km} km` : undefined,
    participants: event.current_participants,
    maxParticipants: event.max_participants,
    price: formatPrice(event.price_cents, event.currency),
    image: event.image_url || 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800&auto=format&fit=crop',
    communityVibe: event.community_vibe,
    afterActivity: event.after_activity,
    participationType: event.participation_type,
  })) || [];

  return (
    <section className="relative py-16 md:py-32 overflow-x-hidden bg-background">
      {/* Diagonal top divider */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-background z-20 hidden md:block" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 100%)' }} />

      {/* Track lanes background */}
      <div className="absolute inset-0 track-lanes opacity-10" />

      {/* Diagonal stripes accent - using accent color */}
      <div className="absolute top-20 left-0 w-[800px] h-[800px] diagonal-stripes opacity-20 rounded-full blur-3xl hidden md:block" />
      <div className="absolute bottom-20 right-0 w-[600px] h-[600px] bg-energy/10 rounded-full blur-3xl hidden md:block" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-20 gap-6 md:gap-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-accent font-bold text-xs sm:text-sm uppercase tracking-widest mb-4 md:mb-6 px-3 md:px-4 py-1 border border-accent/30 rounded-full bg-accent/5 transform -skew-x-6">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 transform skew-x-6" />
              <span className="transform skew-x-6">Sorties entre passionnés</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-7xl lg:text-8xl text-foreground mb-4 md:mb-6 tracking-tighter uppercase italic">
              Runs <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-energy">Communautaires</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-xl leading-relaxed">
              Rejoins la communauté ! Des sorties conviviales organisées par des coureurs comme toi, 
              pour le plaisir de courir ensemble. 🏃‍♂️
            </p>
          </div>

          <Button variant="outline" size="lg" className="hidden md:flex group border-accent/50 hover:border-accent hover:bg-accent/10 text-accent transform -skew-x-6" asChild>
            <Link to="/communaute">
              <span className="transform skew-x-6 flex items-center">
                Voir toutes les sorties
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform duration-300" />
              </span>
            </Link>
          </Button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-accent animate-spin" />
          </div>
        )}

        {/* Events Grid */}
        {!isLoading && events.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mb-8 md:mb-16">
            {events.map((event, index) => (
              <div key={event.id || index}>
                <CommunityEventCard {...event} />
              </div>
            ))}
          </div>
        )}

        {/* Mobile CTA */}
        <div className="text-center md:hidden">
          <Button variant="outline" size="lg" className="w-full group border-accent/50 hover:border-accent hover:bg-accent/10 text-accent" asChild>
            <Link to="/communaute">
              Voir toutes les sorties
              <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Bottom track line - accent color */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent/50 to-transparent" />
    </section>
  );
};

export default CommunityRunsSection;
